import React from 'react';
import { NotificationsProps, Notification } from '../../types/game.types';
import { useGameManager } from '../../hooks/useGameManager';
import { CloseButton } from '../common';
import { useOpenDrawers } from '../../hooks/useOpenDrawers.js';
import { useAudioPlaybackBlocked } from '../../hooks/useAudioPlaybackBlocked.js';
import { VIDEO_CONFIG } from '../../config/videoConfig.js';
import { 
  FaCheckCircle, 
  FaExclamationCircle, 
  FaExclamationTriangle, 
  FaInfoCircle
} from 'react-icons/fa';

interface ToastProps {
  notification: Notification;
  onClose: (id: string) => void;
  /** Entry animation: slide in from the edge the stack grows away from. */
  enterClass: string;
}

const Toast: React.FC<ToastProps> = ({ notification, onClose, enterClass }) => {
  const handleClose = () => {
    onClose(notification.id);
  };

  const getTypeConfig = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return {
          icon: <FaCheckCircle />,
          bgClass: 'bg-game-green/95',
          borderClass: 'border-game-light',
          textClass: 'text-white'
        };
      case 'error':
        return {
          icon: <FaExclamationCircle />,
          bgClass: 'bg-game-orange/95',
          borderClass: 'border-game-golden',
          textClass: 'text-white'
        };
      case 'warning':
        return {
          icon: <FaExclamationTriangle />,
          bgClass: 'bg-game-golden/95',
          borderClass: 'border-game-accent',
          textClass: 'text-gray-800'
        };
      case 'info':
      default:
        return {
          icon: <FaInfoCircle />,
          bgClass: 'bg-game-medium/95',
          borderClass: 'border-game-light',
          textClass: 'text-white'
        };
    }
  };

  const config = getTypeConfig(notification.type);

  return (
    <div className={`flex items-center p-4 rounded-lg shadow-lg backdrop-blur-md pointer-events-auto max-w-[400px] animate-in ${enterClass} ${config.bgClass} border ${config.borderClass} ${config.textClass}`}>
      <div className="flex items-center w-full gap-3">
        <div className="flex-shrink-0 text-lg font-bold w-6 h-6 flex items-center justify-center">
          {config.icon}
        </div>
        <div className="flex-1 text-sm font-medium leading-relaxed">
          {notification.message}
        </div>
        <CloseButton
          size="sm"
          onClick={handleClose}
          className="flex-shrink-0 rounded-full hover:bg-white/20"
        />
      </div>
    </div>
  );
};

/**
 * The toast stack.
 *
 * Toasts live in the lower-right corner, just above the bottom bar, and stack
 * upward: the newest sits in the corner and pushes the older ones up. It is a
 * project-wide rule (`frontend/docs/DESIGN_SYSTEM.md`, "Toasts"; #131), and
 * the corner has two neighbours it must never cover:
 *
 * - the audio-unblock control (`VIDEO_CONFIG.position.audioUnblock`), the
 *   only way to hear a call when the browser blocks autoplay: while it is
 *   showing, the stack starts above it;
 * - the customize-island drawer on the right edge: while it is open, the
 *   stack sits to its left (it is `md:w-[400px]`; below `md` it is full
 *   width and covers the corner anyway).
 */
const Notifications: React.FC<NotificationsProps> = ({ 
  className, 
  position = 'bottom-right' 
}) => {
  const { notifications, removeNotification } = useGameManager();
  const { decorations: isDecorationDrawerOpen } = useOpenDrawers();
  const isAudioPlaybackBlocked = useAudioPlaybackBlocked();

  if (notifications.length === 0) {
    return null;
  }

  const getPositionClasses = (pos: string) => {
    switch (pos) {
      case 'top-right':
        return 'top-5 right-5';
      case 'top-left':
        return 'top-5 left-5';
      case 'bottom-left':
        return 'bottom-24 left-5'; // Above user info bar
      case 'center':
        return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
      case 'bottom-right':
      default: {
        // Above user info bar, or above the audio-unblock control while it shows
        const bottom = isAudioPlaybackBlocked ? VIDEO_CONFIG.position.toastsAboveAudioUnblock : 'bottom-24';
        const right = isDecorationDrawerOpen ? 'right-5 md:right-[420px]' : 'right-5';
        return `${bottom} ${right}`;
      }
    }
  };

  const enterClass = position.startsWith('top') ? 'slide-in-from-top-2' : 'slide-in-from-bottom-2';

  return (
    <div
      data-testid="toast-stack"
      className={`fixed pointer-events-none z-[2000] flex flex-col gap-3 ${getPositionClasses(position)} ${className || ''}`}
    >
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          notification={notification}
          onClose={removeNotification}
          enterClass={enterClass}
        />
      ))}
    </div>
  );
};

export default Notifications;